import * as React from "react";
import { 
    gql, 
    useMutation, 
    ApolloError 
} from "@apollo/client";
import { 
    UpdateCampaignMutationMutation,
    UpdateCampaignMutationMutationVariables 
} from "../../../../../schemaTypes";

export const updateCampaignMutation = gql`
  mutation UpdateCampaignMutation(
    $campaignId: String!
    $input: UpdateCampaignInput!
  ) {
    updateCampaign(
      campaignId: $campaignId,
      input: $input
    ) {
      errors {
        path
        message
      }
      message
      bannerUrl
    }
  }
`;

interface Props {
  onMessage?: (message: string) => void;
  children: (
    data: {
      submit: (
        values: UpdateCampaignMutationMutationVariables & { bannerFile?: File }
      ) => Promise<{
        errors: ({ path: string; message: string } | null)[] | null;
        message?: string;
        bannerUrl?: string;
      }>;
      loading: boolean;
      error?: ApolloError;
    }
  ) => React.ReactElement | null;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export const UpdateCampaignController: React.FC<Props> = ({ children, onMessage }) => {
  const [updateCampaign, { loading, error }] = useMutation<
    UpdateCampaignMutationMutation,
    UpdateCampaignMutationMutationVariables
  >(updateCampaignMutation);

  const normalizeIds = (ids?: (string | null)[] | null) => {
    if (!Array.isArray(ids)) {
      return ids;
    }

    return ids
      .filter((id): id is string => typeof id === "string")
      .map((id) => id.trim())
      .filter(Boolean);
  };

  const submit = async (
    values: UpdateCampaignMutationMutationVariables & {
      input: UpdateCampaignMutationMutationVariables["input"] & {
        bannerFile?: File;
      };
    }
  ) => {
    const { bannerFile, ...inputRest } = values.input;
    const locationId = normalizeIds(inputRest.locationId as (string | null)[] | null | undefined);
    const communityId = normalizeIds(inputRest.communityId as (string | null)[] | null | undefined);

    if (Array.isArray(inputRest.locationId) && Array.isArray(locationId) && locationId.length === 0) {
      return {
        errors: [{ path: "locationId", message: "At least one location is required" }],
        bannerUrl: undefined,
        message: undefined,
      };
    }

    if (Array.isArray(inputRest.communityId) && Array.isArray(communityId) && communityId.length === 0) {
      return {
        errors: [{ path: "communityId", message: "At least one community is required" }],
        bannerUrl: undefined,
        message: undefined,
      };
    }

    let bannerUrl = inputRest.bannerUrl;

    if (bannerFile) {
      bannerUrl = await fileToBase64(bannerFile);
    }

    const { data } = await updateCampaign({
      variables: {
        campaignId: values.campaignId,
        input: {
          ...inputRest,
          locationId,
          communityId,
          bannerUrl,
        },
      },
    });

    const errors = data?.updateCampaign?.errors ?? null;
    const message = data?.updateCampaign?.message ?? undefined;
    const bannerUrlResp = data?.updateCampaign?.bannerUrl ?? undefined;

    if (message && onMessage) {
      onMessage(message);
    }

    return {
      errors,
      bannerUrl: bannerUrlResp,
      message,
    };
  };

  return children({ submit, loading, error });
};